import { useCallback, useState, type FormEvent } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { returnPath } from "@/auth/return-path"
import { useAuth } from "@/auth/useAuth"
import { accountsApi } from "@/lib/accounts-api"
import { getApiErrorMessage } from "@/lib/error-codes"
import {
  forgotPasswordSchema,
  loginSchema,
  type LoginFieldErrors,
  type LoginFormValues,
} from "@/components/auth/login-schemas"

type LoginMode = "login" | "forgot"

function parseLoginFieldErrors(issues: { path: PropertyKey[]; message: string }[]): LoginFieldErrors {
  const errors: LoginFieldErrors = {}

  for (const issue of issues) {
    const key = issue.path[0]
    // zod reports every failed check of a field; the first one says what is wrong ("Введите почту" before "некорректный").
    if ((key === "email" || key === "password") && !errors[key]) {
      errors[key] = issue.message
    }
  }

  return errors
}

export function useLoginFlow() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isLoading, login, error: sessionError } = useAuth()

  const [mode, setMode] = useState<LoginMode>("login")
  const [form, setForm] = useState<LoginFormValues>({ email: "", password: "" })
  const [forgotEmail, setForgotEmail] = useState("")
  const [isForgotLoading, setIsForgotLoading] = useState(false)
  const [forgotError, setForgotError] = useState<string | null>(null)
  const [forgotEmailError, setForgotEmailError] = useState<string | null>(null)
  const [forgotSuccess, setForgotSuccess] = useState<string | null>(null)
  // Why the customer is here again — an expired session — until they start a new sign-in.
  const [submitError, setSubmitError] = useState<string | null>(sessionError)
  const [fieldErrors, setFieldErrors] = useState<LoginFieldErrors>({})

  const resetForgotState = useCallback(() => {
    setForgotError(null)
    setForgotEmailError(null)
    setForgotSuccess(null)
  }, [])

  const openForgotMode = useCallback(() => {
    setMode("forgot")
    resetForgotState()
    setForgotEmail(form.email)
  }, [form.email, resetForgotState])

  const closeForgotMode = useCallback(() => {
    setMode("login")
    resetForgotState()
  }, [resetForgotState])

  const updateFormField = useCallback((field: keyof LoginFormValues, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev))
    setSubmitError(null)
  }, [])

  const updateForgotEmail = useCallback((value: string) => {
    setForgotEmail(value)
    setForgotEmailError(null)
    setForgotError(null)
  }, [])

  const handleLoginSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      setSubmitError(null)
      setFieldErrors({})

      const parsed = loginSchema.safeParse(form)
      if (!parsed.success) {
        setFieldErrors(parseLoginFieldErrors(parsed.error.issues))
        return
      }

      try {
        await login(parsed.data)

        if (accountsApi.hasActiveCustomerSession()) {
          navigate(returnPath(location.state), { replace: true })
        }
      } catch (error) {
        setSubmitError(getApiErrorMessage(error))
      }
    },
    [form, login, location.state, navigate],
  )

  const handleForgotSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      setForgotError(null)
      setForgotEmailError(null)
      setForgotSuccess(null)

      const parsed = forgotPasswordSchema.safeParse({ email: forgotEmail })
      if (!parsed.success) {
        setForgotEmailError(parsed.error.issues[0]?.message ?? "Введите корректный email")
        return
      }

      setIsForgotLoading(true)

      try {
        await accountsApi.forgotPassword(parsed.data.email)
        setForgotSuccess("Если email зарегистрирован, отправили письмо для восстановления.")
      } catch (error) {
        setForgotError(getApiErrorMessage(error))
      } finally {
        setIsForgotLoading(false)
      }
    },
    [forgotEmail],
  )

  const goToHome = useCallback(() => {
    navigate("/")
  }, [navigate])

  const goToRent = useCallback(() => {
    navigate("/rent")
  }, [navigate])

  return {
    mode,
    form,
    forgotEmail,
    isLoading,
    isForgotLoading,
    forgotError,
    forgotEmailError,
    forgotSuccess,
    submitError,
    fieldErrors,
    openForgotMode,
    closeForgotMode,
    updateFormField,
    updateForgotEmail,
    handleLoginSubmit,
    handleForgotSubmit,
    goToHome,
    goToRent,
  }
}
